import { Button, Callout, Card, Flex, Text } from '@radix-ui/themes';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Lists } from '../../components/common/Lists';
import TableComponent from '../../components/common/Tables';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetCurrentAcademicYearQuery } from '../../hooks/useGetCurrentAcademicYearQuery';
import { useGetStudentClassesQuery } from '../../hooks/useGetStudentClassesQuery';
import { useGetTeacheIdByUserId } from '../../hooks/useGetTeacheIdByUserId';
import { setPreviousPath } from '../../redux/slices/previousPath';
import { setPdfPreviewType } from '../../redux/slices/report/report';
import { setStudentListData } from '../../redux/slices/school/student';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { getTeacherClasses, getTeacherSubjects } from '../../services/school/teacher/getTeachers';
import { RegimeOptions } from '../../utils/studentUtils';
import { StudentClassType } from '../../types/Student';
import { useLogoBase64 } from '../../hooks/useLogoBase64';
import { useExportDocument } from '../../hooks/useExportDocument';
import toast from 'react-hot-toast';


type TemplateData = {
  type: string;
  outputFormat: string;
  data: Data;
  logoBase64: string;
}

type Data ={
  cargo: string;
  nome_completo: string;
  info: string;
  student_classes: Studentclass[];
}

type Studentclass = {
  ordem: string;
  codigo: string;
  nome: string;
  apelido: string;
}
const columnNamesMap: { [key: string]: string } = {
  student: 'Estudante',
};

export const TeacherClasses = () => {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userData = useSelector((state: RootState) => state.login.userData);
  const {logoBase64} = useLogoBase64();
  const { exportDocument } = useExportDocument();
  const [currentPage, setCurrentPage] = useState(1);

  const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();

  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });

  const watchInputs = watch();
  const { UserId } = useSelector((state: RootState) => state.login.userData)
  const { data: teacherData } = useGetTeacheIdByUserId(UserId);
  const { isLoading: isLoadingClasses, refetch: fetchTeacherClasses, data: classes, isError: isErrorTeacherClasses } = useQuery(
    ['fetchTeacherClasses'],
    async () => {
      try {
        const classes = await getTeacherClasses({ PageNumber: 1, PageSize: 400, IdTeacher: teacherData?.idTeacher });
        return classes;
      } catch (error) {
      }
    },
    {
      enabled: !!teacherData?.idTeacher,
    }
  );
  const { isLoading: isLoadingSubjects, refetch: fetchTeacherSubjects, data: subjects, isError: isErrorTeacherSubjects } = useQuery(
    ['fetchTeacherSubjects'],
    async () => {
      try {
        const subjects = await getTeacherSubjects({ PageNumber: 1, PageSize: 400, IdTeacher: teacherData?.idTeacher, IdClass: getValues('idClass') });
        return subjects;
      } catch (error) {
      }
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false
    }
  );
  
  const { data: studentsClassData, status: statusStudentsClass, isLoading: isLoadingStudentsClass, refetch: refreshStudentsClass } = useGetStudentClassesQuery({
    PageSize: 400,
    PageNumber: 1,
    IdClass: getValues('idClass'),
    IdSubject: getValues('idSubject'),
    IdTeacher: teacherData?.idTeacher
  },
    {
      enabled: !!watchInputs,//!!getValues('idClass') && !!teacherData?.idTeacher,
    });

  const onSubmit = handleSubmit(async (data) => {
  });

  const [sortedStudentsClass, setSortedStudentsClass] = useState<StudentClassType[] | null>(null);

  useEffect(() => {
    if (studentsClassData) {
      const sortedData = [...studentsClassData.items].sort((a, b) =>
        (a.student || "").localeCompare(b.student || "")
      );
      setSortedStudentsClass(sortedData);
    }
  }, [studentsClassData]);

  if (!teacherData || !teacherData.idTeacher) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Entre como docente &nbsp;
            <Link to={routes.dashboard} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Text>
        </Callout.Root>

      </Flex>
    );
  }
  if (!currentAcademicYear || !currentAcademicYear?.status) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Por favor active o ano lectivo
            <Link to={routes.academicYears} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
          </Text>
        </Callout.Root>

      </Flex>
    );
  }

  const handleChangeClass = async (idClass: string) => {
    setValue('idClass', idClass);
    const _class = classes?.items.find(i => i.id === idClass);
    setValue("semester", _class?.semester ?? 0)
    fetchTeacherSubjects()
    refreshStudentsClass()
  };
  const handleChangeSubject = (idSubject: string) => {
    setValue('idSubject', idSubject);
    refreshStudentsClass()
  }
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };


  const handleOnClickPrintOld = (event: any) => {
    event.preventDefault();
    const classData = classes?.items.find(i => i.id === getValues('idClass'));
    dispatch(setPreviousPath(routes.teacherClasses));
    dispatch(setPdfPreviewType('STUDENTS_LIST'))
    dispatch(setStudentListData({
      list: studentsClassData?.items.map(i => { return { SchoolCode: i.studentCode, Fullname: i.student?.toUpperCase() } }),
      filters: {
        AcademicYear: classData?.academicYear ?? "",
        Regime: RegimeOptions[classData?.regime ?? "1"] ?? "",
        Course: classData?.course ?? "",
        // Departament: classData?.description ?? "",
        Class: classData?.description ?? "",
        // Faculty: faculties?.items.find(i=> i.id===getValues('IdFaculty'))?.description??"",
        // University: universities?.items.find(i=> i.id===getValues('IdUniversity'))?.description??"",
        //  Subject: planSubjectsData?.items.find(i=> i.idSubject===getValues('IdSubject'))?.subject??"",
        // Level: getValues('Level')??"",
        // Semester: getValues('Semester')??"", 
        TotalItems: studentsClassData?.totalItems ?? 0,
        TotalOnPage: studentsClassData?.items?.length ?? 0,
        TotalPages: studentsClassData?.totalPages,
        PageNumber: currentPage,
      }
    }));
    navigate(routes.pdfPreview);
  }
    const handleOnClickPrint = async(event: any) => {
      event.preventDefault();
          const classData = classes?.items.find(i => i.id === getValues('idClass'));
      if (studentsClassData && studentsClassData.items && studentsClassData.items?.length > 0) {
        const data: TemplateData = {
          type: "STUDENT_CLASSES",
          outputFormat: "DOCX",
          data: {
            cargo: Array.isArray(userData?.Role) ? userData?.Role.join(", ") : userData?.Role ?? "",
            nome_completo: userData.Fullname ?? "" ,
            info: `${classData?.description} - ${classData?.academicYear} - ${RegimeOptions[classData?.regime ?? "1"] ?? ""} - ${classData?.level ?? ""}º Ano - ${classData?.semester ?? ""}º Semestre - ${classData?.course ?? ""} - ${classData?.departament ?? ""}`,
            student_classes: studentsClassData.items.map((i, index) => ({
              ordem: (index + 1).toString(),
              codigo: i.studentCode ?? "",
              nome: i.student ?? "",
              apelido: i.student?.split(" ")[1] ?? ""
            }))
          },
          logoBase64: logoBase64 ?? ''
         }
        await exportDocument(data, 'Lista_Estudantes_Turma_');
        return;
        }
        toast.error("Sem estudantes na turma");
      }



  return (
    <Card>
      <Flex direction='column' gap='4'>

        <form onSubmit={onSubmit}>
          <Flex gap='3' direction='column'>
            <Card size='1'>
              <Flex direction='column' gap="2">
                <Flex gap='3'>
                  <SelectComponent name="idClass" label="Turma" control={control} errors={errors} style={{ width: '100%' }}
                    options={classes?.items.map(_class => {
                      return {
                        label: _class.description,
                        value: _class.id ?? ""
                      }
                    })}
                    onSelect={handleChangeClass}
                    itemsLoading={isLoadingClasses}
                    itemsError={isErrorTeacherClasses}
                  />
                  <SelectComponent name="idSubject" label="Disciplina" control={control} errors={errors} style={{ width: '100%' }}
                    options={subjects?.items.map(subject => {
                      return {
                        label: subject.description,
                        value: subject.id ?? ""
                      }
                    })}
                    itemsLoading={isLoadingSubjects}
                    itemsError={isErrorTeacherSubjects}
                    onSelect={handleChangeSubject}
                  />
                </Flex>
              </Flex>
            </Card>

            <Flex direction="column" gap="4" style={{ flex: 1 }}>
              {(getValues('idClass') || getValues('idSubject')) && (
                <Lists
                  status={statusStudentsClass}
                  TableComponent={() => <TableComponent
                    columns={Object.keys(columnNamesMap)}
                    items={sortedStudentsClass ?? studentsClassData?.items}
                    options={[]}
                    columnNamesMap={columnNamesMap}
                    totalPages={studentsClassData?.totalPages || 1}
                    currentPage={1}
                    onPageChange={handlePageChange} />} />

              )}
            </Flex>

            <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>


              <Button onClick={handleOnClickPrint} variant='surface'
              >
                Extrair
              </Button>
            </Flex>

          </Flex>
        </form>
      </Flex>
    </Card>
  )
}
